import { Checkbox } from '@/components/ui/checkbox';
import { useStartCardSetupAction } from '@/hooks/actions/use-start-card-setup-action';
import { BillingTargetChangedError } from '@/hooks/actions/use-billing-action-scope';
import { Button, Stack } from '@chakra-ui/react';
import type { OrganizationBillingContext, User } from '@fluenceur/types';
import { NoticePanel } from '@fluenceur/ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BillingIdentityFields, useBillingIdentity } from './BillingIdentityFields';

export function BillingCardForm({
  user,
  context,
  refresh,
}: {
  user: User;
  context: OrganizationBillingContext;
  refresh: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const start = useStartCardSetupAction();
  const contact = useBillingIdentity(user, context.cardSetup.identityRequired);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [failure, setFailure] = useState<'rate' | 'error' | null>(null);
  const inFlight = useRef(false);
  const operation = context.cardSetup.operation;
  const resume = !!operation;
  const needsIdentity = context.cardSetup.identityRequired;
  const identityValid = contact.valid;
  const pending =
    waiting ||
    ['preparing', 'confirming', 'needs_reconciliation'].includes(operation?.status ?? '');
  const blocked =
    operation?.status === 'blocked' ||
    (context.cardSetup.retryAfterSeconds ?? 0) > 0 ||
    failure === 'rate';
  const check = async () => {
    await refresh();
    setWaiting(false);
    setFailure(null);
  };
  const submit = async () => {
    if (
      !consent ||
      !identityValid ||
      inFlight.current ||
      !context.checkoutAvailability.freeCardSetup
    )
      return;
    inFlight.current = true;
    setBusy(true);
    setFailure(null);
    try {
      const result = await start.mutateAsync(
        needsIdentity
          ? {
              identity: contact.identity,
              consent: true,
            }
          : { consent: true }
      );
      if (result.kind === 'redirect') {
        window.location.href = result.url;
        return;
      }
      setWaiting(true);
      await refresh();
    } catch (error) {
      if (error instanceof BillingTargetChangedError) return;
      const status = (error as { response?: { status?: number } }).response?.status;
      setFailure(status === 429 ? 'rate' : 'error');
      await refresh();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  if (blocked)
    return (
      <NoticePanel
        tone="warning"
        title={t('onboarding.billing.rateLimitedBody')}
        actions={
          <Button variant="outline" onClick={() => void check()}>
            {t('common.retry')}
          </Button>
        }
      />
    );
  if (pending)
    return (
      <NoticePanel
        tone="info"
        title={t('onboarding.billing.pendingTitle')}
        actions={
          <Button variant="outline" onClick={() => void check()}>
            {t('onboarding.billing.checkCta')}
          </Button>
        }
      >
        {t('onboarding.billing.pendingBody')}
      </NoticePanel>
    );
  return (
    <Stack gap={4}>
      <NoticePanel tone="info" title={t('onboarding.billing.cardTitle')}>
        {t('onboarding.billing.cardBody')}
      </NoticePanel>
      {operation?.status === 'expired' && (
        <NoticePanel tone="warning" title={t('onboarding.billing.expiredBody')} />
      )}
      {failure === 'error' && (
        <NoticePanel tone="warning" title={t('onboarding.billing.failedBody')} />
      )}
      {needsIdentity && <BillingIdentityFields value={contact} />}
      <Checkbox checked={consent} onCheckedChange={({ checked }) => setConsent(checked === true)}>
        {t('onboarding.billing.consent')}
      </Checkbox>
      <Button
        onClick={() => void submit()}
        loading={busy}
        disabled={!consent || !identityValid || !context.checkoutAvailability.freeCardSetup}
      >
        {t(resume ? 'onboarding.billing.resumeCta' : 'onboarding.billing.setupCta')}
      </Button>
    </Stack>
  );
}
